import { useState, useMemo, type CSSProperties } from 'react';
import { Camera, User as UserIcon } from 'lucide-react';
import type { TipoPerfil } from '../../types/models';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | number;

export interface UserAvatarProps {
  /** URL da imagem (fotoUrl, avatarUrl, base64 ou URL pública) */
  src?: string | null;
  /** Nome completo ou parcial para geração de iniciais */
  name?: string | null;
  /** Perfil ou cargo para definir a paleta de cores do gradiente de fallback */
  role?: TipoPerfil | string | null;
  /** Gênero do usuário (opcional, para adequação de cor em perfis específicos) */
  genero?: 'MASCULINO' | 'FEMININO' | null;
  /** Tamanho predefinido ou numérico em pixels */
  size?: AvatarSize;
  /** Formato da borda: circular (padrão) ou com cantos arredondados (squircle) */
  shape?: 'circle' | 'rounded' | 'square';
  /** Borda decorativa customizada (ex: '2px solid #3b82f6') */
  border?: string;
  /** Indicador de status (ex: bolinha verde de online ou badge de risco) */
  status?: 'online' | 'offline' | 'busy' | 'em_risco' | null;
  /** Se true, adiciona um overlay com ícone de câmera para acionar upload */
  editable?: boolean;
  /** Se o upload está em andamento */
  isUploading?: boolean;
  /** Callback acionado ao clicar no avatar */
  onClick?: () => void;
  /** Callback acionado especificamente ao clicar no botão de editar/câmera */
  onEditClick?: () => void;
  /** Classes CSS adicionais */
  className?: string;
  /** Estilos inline complementares */
  style?: CSSProperties;
  /** Texto alternativo acessível */
  alt?: string;
}

const SIZE_MAP: Record<string, { size: number; fontSize: number; iconSize: number; camSize: number }> = {
  xs: { size: 24, fontSize: 11, iconSize: 13, camSize: 10 },
  sm: { size: 32, fontSize: 14, iconSize: 17, camSize: 12 },
  md: { size: 40, fontSize: 17, iconSize: 21, camSize: 14 },
  lg: { size: 56, fontSize: 24, iconSize: 28, camSize: 18 },
  xl: { size: 80, fontSize: 34, iconSize: 40, camSize: 22 },
  '2xl': { size: 120, fontSize: 50, iconSize: 58, camSize: 28 },
};

export function isRealPhoto(url?: string | null): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim().toLowerCase();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return false;
  if (trimmed.includes('i.pravatar.cc') || trimmed.includes('ui-avatars.com')) return false;
  return trimmed.startsWith('data:image/') || trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/');
}

export function extractInitials(name?: string | null): string {
  if (!name || typeof name !== 'string') return '';
  const cleaned = name
    .replace(/^(Prof\.|Profa\.|Profª|Profº|Dr\.|Dra\.|Sr\.|Sra\.)\s+/i, '')
    .trim();
  if (!cleaned) return '';
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function getRoleGradient(role?: string | null): string {
  const norm = String(role || '').toUpperCase();
  if (norm.includes('MASTER')) return 'linear-gradient(135deg, #4338ca, #312e81)';
  if (norm.includes('ADMIN') || norm === 'DIRETOR' || norm.includes('DIR')) return 'linear-gradient(135deg, #1e40af, #1d4ed8)';
  if (norm.includes('COORD')) return 'linear-gradient(135deg, #0d9488, #0f766e)';
  if (norm.includes('SEC')) return 'linear-gradient(135deg, #d97706, #b45309)';
  if (norm.includes('PROF')) return 'linear-gradient(135deg, #059669, #047857)';
  if (norm.includes('RESP')) return 'linear-gradient(135deg, #2563eb, #1e40af)';
  if (norm.includes('ALUN')) return 'linear-gradient(135deg, #7c3aed, #6d28d9)';
  if (norm.includes('MUN')) return 'linear-gradient(135deg, #0891b2, #0e7490)';
  if (norm.includes('EST')) return 'linear-gradient(135deg, #4f46e5, #4338ca)';
  if (norm.includes('RED')) return 'linear-gradient(135deg, #6366f1, #4f46e5)';
  return 'linear-gradient(135deg, #3b82f6, #1d4ed8)';
}

export function UserAvatar({
  src,
  name,
  role,
  genero,
  size = 'md',
  shape = 'circle',
  border,
  status,
  editable = false,
  isUploading = false,
  onClick,
  onEditClick,
  className = '',
  style = {},
  alt,
}: UserAvatarProps) {
  const [hasImageError, setHasImageError] = useState(false);

  // Determinar dimensões e proporções tipográficas
  const { pxSize, fontSize, iconSize, camSize } = useMemo(() => {
    const px = typeof size === 'number' ? size : (SIZE_MAP[size]?.size || 40);
    const fs = typeof size === 'number'
      ? Math.round(px * 0.42)
      : (SIZE_MAP[size]?.fontSize || Math.round(px * 0.42));
    const is = typeof size === 'number'
      ? Math.round(px * 0.48)
      : (SIZE_MAP[size]?.iconSize || Math.round(px * 0.48));
    const cs = typeof size === 'number'
      ? Math.round(Math.max(12, px * 0.25))
      : (SIZE_MAP[size]?.camSize || 14);

    return {
      pxSize: px,
      fontSize: fs,
      iconSize: is,
      camSize: cs,
    };
  }, [size]);

  const initials = useMemo(() => extractInitials(name), [name]);
  const hasValidPhoto = isRealPhoto(src) && !hasImageError;
  const gradient = useMemo(() => getRoleGradient(role), [role]);

  // Escala dinâmica das letras acompanhando harmonicamente o tamanho do círculo
  const calculatedFontSize = useMemo(() => {
    if (!initials) return fontSize;
    if (initials.length === 1) return Math.round(pxSize * 0.52);
    return Math.round(pxSize * 0.46);
  }, [initials, pxSize, fontSize]);

  const borderRadius = shape === 'circle' ? '50%' : shape === 'rounded' ? '12px' : '4px';

  return (
    <div
      className={`ac-user-avatar-root ${className}`}
      onClick={onClick}
      style={{
        position: 'relative',
        width: pxSize,
        height: pxSize,
        borderRadius,
        background: 'transparent',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: onClick || editable ? 'pointer' : 'default',
        userSelect: 'none',
        ...style,
      }}
      title={name || alt || undefined}
    >
      {/* Container Principal do Avatar */}
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius,
          border: border || (shape === 'circle' ? '2px solid rgba(255, 255, 255, 0.9)' : undefined),
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          background: hasValidPhoto ? '#e2e8f0' : gradient,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          fontWeight: 800,
          fontSize: `${calculatedFontSize}px`,
          position: 'relative',
        }}
      >
        {hasValidPhoto ? (
          <img
            src={src!}
            alt={alt || name || 'Avatar'}
            onError={() => setHasImageError(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : initials ? (
          <span
            style={{
              fontSize: `${calculatedFontSize}px`,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: initials.length > 1 ? '-0.02em' : 'normal',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textTransform: 'uppercase',
            }}
          >
            {initials}
          </span>
        ) : (
          <UserIcon size={iconSize} style={{ opacity: 0.9 }} />
        )}

        {/* Overlay durante upload */}
        {isUploading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(2px)',
            }}
          >
            <div
              style={{
                width: Math.max(14, pxSize * 0.3),
                height: Math.max(14, pxSize * 0.3),
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTopColor: '#ffffff',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
          </div>
        )}
      </div>

      {/* Botão Flutuante de Edição / Câmera */}
      {editable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (onEditClick) onEditClick();
            else if (onClick) onClick();
          }}
          style={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            width: camSize + 10,
            height: camSize + 10,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
            cursor: 'pointer',
            padding: 0,
            transition: 'transform 0.15s ease',
          }}
          title="Alterar foto de perfil"
        >
          <Camera size={camSize} />
        </button>
      )}

      {/* Indicador de Status */}
      {status && (
        <span
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: Math.max(8, pxSize * 0.22),
            height: Math.max(8, pxSize * 0.22),
            borderRadius: '50%',
            border: '2px solid #ffffff',
            background:
              status === 'online'
                ? '#10b981'
                : status === 'busy'
                  ? '#ef4444'
                  : status === 'em_risco'
                    ? '#f59e0b'
                    : '#94a3b8',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        />
      )}
    </div>
  );
}
